import { useCallback, useEffect, useRef, useState } from "react";
import { Link2, X } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { Link, useNavigate } from "@tanstack/react-router";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/use-toast";
import { PostHogEvents, useTrackEvent } from "@/lib/posthog-events";
import { useResolveUserReport } from "@/lib/api/user-reports";
import { getMenteeDisplayName } from "@/lib/redacted-display";
import {
  DrawerField,
  Divider,
  FeedbackPrompt,
  ResolveForm,
} from "../drawer-sections";
import { StatusPill, PriorityPill } from "../pills";
import { DirectionPill } from "./direction-pill";
import {
  DIRECTION_LABELS,
  type UserReport,
  type UserReportUser,
} from "./types";

interface ReportDrawerProps {
  report: UserReport;
  onClose: () => void;
}

function InfoRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-xs text-[#58534c]">{label}</span>
      <span className="text-sm text-[#1f1e1d]">{value}</span>
    </div>
  );
}

function UserLink({ user }: { user: UserReportUser | null }) {
  const name = (user ? getMenteeDisplayName(user) : "") || "Unknown";
  return user?.id ? (
    <Link
      to="/users/$userId"
      params={{ userId: user.id }}
      className="text-[#1f1e1d] underline underline-offset-2 hover:text-black"
    >
      {name}
    </Link>
  ) : (
    name
  )
}

function ResolveReportForm({
  report,
  onResolved,
}: {
  report: UserReport;
  onResolved: (resolved: UserReport) => void;
}) {
  const resolveReport = useResolveUserReport();
  const [feedbackCorrect, setFeedbackCorrect] = useState<boolean | null>(null);
  const [note, setNote] = useState("");

  const handleResolve = async () => {
    if (feedbackCorrect === null || resolveReport.isPending) return;
    const resolved = await resolveReport.mutateAsync({
      reportId: report.id,
      feedbackCorrect,
      note,
    });
    onResolved(resolved);
  };

  if (feedbackCorrect === null) {
    return (
      <FeedbackPrompt
        onSelect={setFeedbackCorrect}
        question="Was this reported correctly?"
      />
    );
  }

  return (
    <ResolveForm
      feedbackCorrect={feedbackCorrect}
      feedbackNote={note}
      isPending={resolveReport.isPending}
      onChangeCorrect={setFeedbackCorrect}
      onChangeNote={setNote}
      onResolve={handleResolve}
      correctLabel="Reported correctly"
      notePlaceholder="eg. This was reported correctly..."
    />
  );
}

function ResolvedSummary({ report }: { report: UserReport }) {
  return (
    <div className="flex flex-col gap-3.5">
      <DrawerField label="Reported correctly">
        {report.feedbackCorrect === true
          ? "Yes"
          : report.feedbackCorrect === false
            ? "No"
            : "—"}
      </DrawerField>
      {report.resolutionNote && (
        <DrawerField label="Reviewer notes">{report.resolutionNote}</DrawerField>
      )}
      {report.resolvedBy && (
        <DrawerField label="Reviewed by">
          {report.resolvedBy.name ?? "Unknown"}
        </DrawerField>
      )}
      {report.resolvedAt && (
        <DrawerField label="Resolved">
          {formatDistanceToNow(new Date(report.resolvedAt), {
            addSuffix: true,
          }).replace(/^about /, "")}
        </DrawerField>
      )}
    </div>
  );
}

export function ReportDrawer({ report, onClose }: ReportDrawerProps) {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { track } = useTrackEvent();
  const trackedReportId = useRef<string | null>(null);
  const [resolvedReport, setResolvedReport] = useState<UserReport | null>(null);
  const view = resolvedReport ?? report;

  const trackReportView = useCallback(() => {
    track(PostHogEvents.SUPERVISOR_REPORT_VIEW, {
      report_id: report.id,
      time_to_view_ms: Date.now() - new Date(report.createdAt).getTime(),
    });
  }, [report.id, report.createdAt, track]);

  useEffect(() => {
    if (trackedReportId.current === report.id) return;
    trackedReportId.current = report.id;
    setResolvedReport(null);
    trackReportView();
  }, [report.id, trackReportView]);

  const handleShare = useCallback(async () => {
    const url = `${window.location.origin}/risk-flagging?view=reports&reportId=${report.id}`;
    try {
      await navigator.clipboard.writeText(url);
      toast({
        title: "Link copied",
        description: "Anyone with access can open this report",
      });
    } catch {
      toast({
        title: "Failed to copy link",
        description: "Copy it manually from the address bar",
        variant: "destructive",
      });
    }
  }, [report.id, toast]);

  const handleViewConversation = () => {
    if (!view.mentorshipId) return;
    trackReportView();
    onClose();
    navigate({
      to: "/mentorships/$mentorshipId",
      params: { mentorshipId: view.mentorshipId },
    });
  };

  return (
    <>
      <div className="fixed inset-0 z-50" onClick={onClose} />

      <div className="fixed bottom-6 right-6 top-6 z-50 w-[400px] overflow-y-auto rounded-[10px] border border-[rgba(31,30,29,0.12)] bg-white font-body shadow-xl">
        <div className="flex min-h-full flex-col gap-4 p-6">
          <div className="flex items-start justify-between">
            <div className="flex flex-col gap-1">
              <p className="text-base font-normal text-[#1f1e1d]">
                User report
              </p>
              <p className="text-xs text-[#58534c]">
                {DIRECTION_LABELS[view.direction]} report submitted from this
                conversation
              </p>
            </div>
            <div className="flex shrink-0 items-center gap-1">
              <button
                onClick={handleShare}
                title="Copy link to this report"
                className="rounded-sm p-0.5 opacity-70 hover:opacity-100"
              >
                <Link2 className="h-4 w-4" />
              </button>
              <button
                onClick={onClose}
                className="rounded-sm p-0.5 opacity-70 hover:opacity-100"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          <Divider />

          <div className="flex flex-col gap-2.5">
            <InfoRow
              label="Report ID"
              value={
                <span className="break-all font-mono text-xs text-[#58534c]">
                  {view.id}
                </span>
              }
            />
            <InfoRow
              label="Direction"
              value={<DirectionPill direction={view.direction} />}
            />
            <InfoRow
              label="Tier"
              value={<PriorityPill priority={view.priority} />}
            />
            <InfoRow label="Status" value={<StatusPill status={view.status} />} />
            <InfoRow
              label="Created"
              value={formatDistanceToNow(new Date(view.createdAt), {
                addSuffix: true,
              }).replace(/^about /, "")}
            />
          </div>

          <Divider />

          <div className="flex flex-col gap-2.5">
            <InfoRow
              label="Reporter"
              value={<UserLink user={view.reporter} />}
            />
            <InfoRow
              label="Reported"
              value={<UserLink user={view.reportedUser} />}
            />
          </div>

          <Divider />

          <div className="flex flex-col gap-2">
            <span className="text-xs text-[#58534c]">Category</span>
            <span className="text-sm text-[#1f1e1d]">
              {view.categoryLabel ?? (
                <span className="italic text-zinc-400">
                  No category selected
                </span>
              )}
            </span>
          </div>

          <div className="flex flex-col gap-2">
            <span className="text-xs text-[#58534c]">Reason</span>
            <span className="text-sm text-[#1f1e1d]">
              {view.reason ?? (
                <span className="italic text-zinc-400">No note provided</span>
              )}
            </span>
          </div>

          <Button
            variant="outline"
            className="w-full rounded-[10px]"
            disabled={!view.mentorshipId}
            onClick={handleViewConversation}
          >
            View conversation
          </Button>

          <Divider />

          {view.status === "OPEN" ? (
            <ResolveReportForm report={view} onResolved={setResolvedReport} />
          ) : (
            <ResolvedSummary report={view} />
          )}

        </div>
      </div>
    </>
  );
}
